import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE } from '@klear/bank-account/constants'
import { isWireAddressComplete, missingWireAddressKeys, type WireAddressKey } from '@klear/bank-account/payment-network'
import type { AccountDetails, BillingDetails, SupportedPaymentNetworks } from '@klear/bank-account/schema'
import { Timestamp } from '@klear/time'

export type ExternalBankAccountIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type BaseExternalBankAccountJson = {
  identity: ExternalBankAccountIdentity
  organizationId: number
  accountDetails: AccountDetails
  billingDetails: BillingDetails
  supportedPaymentNetworks: SupportedPaymentNetworks
  createdAt: number | Timestamp
  updatedAt?: number | Timestamp
  deletedAt?: number | Timestamp
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
}

export abstract class BaseExternalBankAccount {
  readonly identity: ExternalBankAccountIdentity
  organizationId: number
  accountDetails: AccountDetails
  billingDetails: BillingDetails
  supportedPaymentNetworks: SupportedPaymentNetworks

  createdAt: Timestamp
  updatedAt: Timestamp
  deletedAt?: Timestamp
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string

  constructor(props: BaseExternalBankAccountJson) {
    const now = Timestamp.now()
    this.identity = props.identity?.kind === 'saved' ? { kind: 'saved', id: props.identity.id } : { kind: 'new' }
    this.organizationId = props.organizationId
    this.accountDetails = props.accountDetails
    this.billingDetails = props.billingDetails
    this.supportedPaymentNetworks = props.supportedPaymentNetworks
    this.createdAt = props.createdAt instanceof Timestamp ? props.createdAt : new Timestamp(props.createdAt ?? now)
    if (props.updatedAt != null) {
      this.updatedAt = props.updatedAt instanceof Timestamp ? props.updatedAt : new Timestamp(props.updatedAt)
    } else {
      this.updatedAt = this.createdAt
    }
    if (props.deletedAt != null) {
      this.deletedAt = props.deletedAt instanceof Timestamp ? props.deletedAt : new Timestamp(props.deletedAt)
    } else {
      this.deletedAt = undefined
    }
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  requireSaved(): asserts this is this & { id: number } {
    if (!this.isSaved) {
      throw new Error(`${this.constructor.name} is not saved`)
    }
  }

  abstract assertValid(): void
  abstract toJSON(): unknown

  get accountNumber(): string {
    return this.accountDetails.account_number
  }
  get last4(): string {
    return this.accountDetails.account_number.slice(-4)
  }
  get routingNumber(): string {
    return this.accountDetails.routing_number
  }
  get bankName(): string {
    return this.accountDetails.name
  }
  get name(): string {
    return this.bankName
  }
  get nameAndLast4(): string {
    return `${this.bankName} - ${this.last4}`
  }
  get accountHolderName(): string {
    return this.billingDetails.name
  }
  get accountHolderType(): AccountDetails['account_holder_type'] {
    return this.accountDetails.account_holder_type
  }
  get accountType(): AccountDetails['account_type'] {
    return this.accountDetails.account_type
  }
  get isWireSupported(): boolean {
    return this.supportedPaymentNetworks.includes(PAYMENT_NETWORK_WIRE)
  }
  get isAchSupported(): boolean {
    return this.supportedPaymentNetworks.includes(PAYMENT_NETWORK_ACH)
  }

  get hasWireAddress(): boolean {
    return isWireAddressComplete(this.billingDetails.address)
  }

  get missingWireAddressFields(): WireAddressKey[] {
    return missingWireAddressKeys(this.billingDetails.address)
  }
}

export function baseExternalBankAccountToJson(account: BaseExternalBankAccount) {
  return {
    identity: account.identity,
    organizationId: account.organizationId,
    accountDetails: account.accountDetails,
    billingDetails: account.billingDetails,
    supportedPaymentNetworks: account.supportedPaymentNetworks,
    createdAt: account.createdAt.epochMs,
    updatedAt: account.updatedAt.epochMs,
    deletedAt: account.deletedAt?.epochMs,
    createdByUserId: account.createdByUserId,
    createdByUserEmail: account.createdByUserEmail,
    updatedByUserId: account.updatedByUserId,
    updatedByUserEmail: account.updatedByUserEmail,
    deletedByUserId: account.deletedByUserId,
    deletedByUserEmail: account.deletedByUserEmail
  }
}
